/* import __COLOCATED_TEMPLATE__ from './sms-sender-selector.hbs'; */
/* RESPONSIBLE TEAM: team-inbox */
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import { type PhoneNumber } from 'embercom/objects/inbox/phone-number';
import type InboxApi from 'embercom/services/inbox-api';
import { countryData } from 'embercom/components/outbound/sms/onboarding/country-data';

interface Args {
  selectedPhoneNumberId: number | undefined;
  onPhoneNumberSelectionChange: (id: number) => void;
}

interface Signature {
  Args: Args;
}

interface CountryGroup {
  countryCode: string;
  countryName: string;
  phoneNumbers: PhoneNumber[];
}

export default class SmsSenderSelector extends Component<Signature> {
  @service declare inboxApi: InboxApi;
  @service declare intercomEventService: any;

  @tracked phoneNumbers: PhoneNumber[] = [];
  @tracked isLoading = false;

  constructor(owner: unknown, args: Args) {
    super(owner, args);
    this.loadPhoneNumbers();
  }

  async loadPhoneNumbers() {
    this.isLoading = true;
    this.phoneNumbers = await this.inboxApi.fetchSmsPhoneNumbers();
    this.isLoading = false;
  }

  get countryGroups(): CountryGroup[] {
    let grouped = new Map<string, PhoneNumber[]>();
    for (let phone of this.phoneNumbers) {
      let existing = grouped.get(phone.countryCode) ?? [];
      existing.push(phone);
      grouped.set(phone.countryCode, existing);
    }

    let groups: CountryGroup[] = [];
    for (let [countryCode, phoneNumbers] of grouped) {
      let data = countryData[countryCode] as { name: string } | undefined;
      groups.push({
        countryCode,
        countryName: data?.name ?? countryCode,
        phoneNumbers,
      });
    }
    return groups;
  }

  get selectedPhoneNumber(): PhoneNumber | undefined {
    if (this.args.selectedPhoneNumberId === undefined) {
      return undefined;
    }
    return this.phoneNumbers.find((p) => p.id === this.args.selectedPhoneNumberId);
  }

  @action selectPhoneNumber(phoneNumber: PhoneNumber, popover: any) {
    this.intercomEventService.trackAnalyticsEvent({
      action: 'selected',
      object: 'sms_sender_phone_number',
      section: 'composer',
      country_code: phoneNumber.countryCode,
    });
    this.args.onPhoneNumberSelectionChange(phoneNumber.id);
    popover?.hide();
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Inbox2::NewConversation::SmsSenderSelector': typeof SmsSenderSelector;
    'inbox2/new-conversation/sms-sender-selector': typeof SmsSenderSelector;
  }
}
